<script setup>
import { defineProps, defineEmits, onMounted, ref, computed } from 'vue';

const emit = defineEmits(['add', 'edit']);

const props = defineProps({
    title: {
        type: String,
        required: false,
        default: ''
    },
    description: {
        type: String,
        required: false,
        default: ''
    },
    price: {
        type: String,
        required: false,
        default: ''
    },
    eval_price: {
        type: String,
        required: false,
        default: ''
    },
    eval_id: {
        type: String,
        required: false,
        default: ''
    },
    currency: {
        type: String,
        required: false,
        default: ''
    },
    file: {
        type: Object,
        required: false,
        default: () => null
    },
    schema: {
        type: Object,
        required: false,
        default: () => null
    },
    image: {
        type: String,
        required: false,
        default: ''
    },
    tags: {
        type: Array,
        required: false,
        default: () => []
    },
    status: {
        type: String,
        required: false,
        default: ''
    },
    type: {
        type: String,
        required: false,
        default: ''
    },
    deliveryMethod: {
        type: String,
        required: false,
        default: ''
    },
    index: {
        type: Number,
        default: 0
    },
    isAddCard: {
        type: Boolean,
        default: false
    },
    showEdit: {
        type: Boolean,
        default: false
    },
    rightTagText : {
        type: String,
        required: false,
    },
    rightTagColor : {
        type: String,
        required: false,
        default: 'gray',
    },
    dataTestId: {
        type: String,
        required: false,
        default: '',
    },
    is_open_source: {
        type: Boolean,
        default: false
    }
});

const isVisible = ref(false);

onMounted(() => {
    setTimeout(() => {
        isVisible.value = true;
    }, 10);
});

const handleClick = () => {
    if (props.isAddCard) {
        emit('add');
    }
};

const handleEdit = (event) => {
    event.stopPropagation();
    emit('edit');
};

const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (bytes == 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

const displayFileName = computed(() => {
    if (props.file?.name) {
        return props.file.name;
    }
    return props.file_name || '';
});

const displayFileSize = computed(() => {
    if (props.file?.size) {
        return formatFileSize(props.file.size);
    }
    return props.file_size || 'fgghg';
});

const getRightTagColor = () => {
    const colorsClasses = {
        default: 'bg-echoes-background-black/50',
        success: 'bg-echoes-primary/50',
        live: 'bg-green-500/50',
        error: 'bg-red-400/50',
        warning: 'bg-amber-300/50',
        info: 'bg-violet-600/50',
    }
    if(props.rightTagColor) {
        return colorsClasses[props.rightTagColor];
    }
    return colorsClasses.gray;
}

const getDeliveryMethod = () => {
    if (props.eval_id) {
        return 'eval';
    }
    return props.deliveryMethod;
}

const formattedPrice = computed(() => {
    if (props.price !== 0 && props.price !== '0' && !props.price) return '';

    const numericValue = parseFloat(String(props.price).replace(/[^\d.-]/g, ''));
    if (isNaN(numericValue)) return props.price;

    const formatOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    };
    const formattedValue = `$${numericValue.toLocaleString('en-US', formatOptions)}`;
    return props.eval_id ? `${formattedValue}/Run` : formattedValue;
});
</script>

<template>
    <div class="card" :class="{
        'card--add': isAddCard,
        'card--visible': isVisible
    }" :style="{ '--card-index': index }" @click="handleClick" :data-testid="dataTestId">
        <div class="card__content">
            <div v-if="isAddCard" class="card__add-content">
                <div class="card__add-button">
                    <span class="card__add-icon"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                            fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M5 12h14m-7 7V5" />
                        </svg>
                    </span>
                </div>
                <p class="card__add-text">Add new item</p>
            </div>
            <template v-else>
                <div class="grid grid-cols-3 w-full md:flex md:flex-col">
                    <div v-if="image" class="card__image h-full w-full col-span-1" :style="{ backgroundImage: `url('${image}')` }">
                        <div class="card__overlay"></div>
                        <div v-if="props.deliveryMethod" class="card__delivery-header w-full">
                            <div class="flex justify-between">
                                <div class="card__delivery-tag">
                                    {{ (getDeliveryMethod() || '').toUpperCase() }}
                                </div>
                                <div class="card__delivery-tag-status" :class="getRightTagColor()" v-if="props.rightTagText">
                                    {{ props.rightTagText.toUpperCase() }}
                                </div>
                            </div>
                        </div>
                        <div v-if="tags && tags.length > 0" class="hidden md:block absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5 justify-end z-[1]">
                            <span v-for="tag in tags.slice(0, 3)" 
                                :key="tag.id || tag.tag_id" 
                                class="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full whitespace-nowrap shadow-md"
                                :style="{
                                    backgroundColor: tag.display_color,
                                    color: '#000000',
                                    boxShadow: `0 1px 2px ${tag.display_color}40`
                                }">
                                {{ tag.name }}
                            </span>
                        </div>
                    </div>
                    <div v-else class="card__image card__image--placeholder w-full col-span-1">
                        <div class="card__overlay"></div>
                        <div v-if="props.deliveryMethod" class="card__delivery-header w-full">
                            <div class="flex justify-between">
                                <div class="card__delivery-tag">
                                    {{ (getDeliveryMethod() || '').toUpperCase() }}
                                </div>
                                <div class="card__delivery-tag-status" :class="getRightTagColor()" v-if="props.rightTagText">
                                    {{ props.rightTagText.toUpperCase() }}
                                </div>
                            </div>
                        </div>
                        <div v-if="tags && tags.length > 0" class="hidden md:block absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5 justify-end z-[1]">
                            <span v-for="tag in tags.slice(0, 3)" 
                                :key="tag.id || tag.tag_id" 
                                class="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full whitespace-nowrap shadow-md"
                                :style="{
                                    backgroundColor: tag.display_color,
                                    color: '#000000',
                                    boxShadow: `0 1px 2px ${tag.display_color}40`
                                }">
                                {{ tag.name }}
                            </span>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="none" viewBox="0 0 24 24" class="card__placeholder-icon">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z M3.27 6.96L12 12.01l8.73-5.05 M12 22.08V12" />
                        </svg>
                        <div class="card__description-overlay">
                            <p class="card__description-text">{{ description }}</p>
                        </div>
                    </div>
                    <div class="card__info col-span-2">
                        <div class="h-6 md:h-[52px]">
                            <h3 class="card__title">{{ title }}</h3>
                        </div>
                        <div v-if="is_open_source || formattedPrice || (props.type === 'auction' && !props.eval_id)" class="card__price-row">
                            <p v-if="is_open_source" class="card__price">Open Source</p>
                            <p v-else-if="props.type === 'auction' && !props.eval_id" class="card__price">Bid Only</p>
                            <p v-else class="card__price">{{ formattedPrice }} {{ currency }}</p>
                            <div class="card__status" :class="{
                                'card__status--active': status === 'active',
                                'card__status--sold': status === 'sold',
                                'card__status--cancelled': status === 'cancelled'
                            }">
                                {{ status.toUpperCase() }}
                            </div>
                        </div>
                        <div v-if="displayFileName" class="card__file-info">
                            <p class="card__file-name">{{ displayFileName }}</p>
                            <p class="card__file-size">{{ displayFileSize }}</p>
                        </div>
    
    
                        <div v-if="tags && tags.length > 0" class=" block md:hidden flex flex-wrap gap-1.5 z-[1] mt-2">
                            <span v-for="tag in tags.slice(0, 3)" 
                                :key="tag.id || tag.tag_id" 
                                class="inline-flex items-center px-2 text-[10px] leading-[1.5] rounded-full whitespace-nowrap shadow-md"
                                :style="{
                                    backgroundColor: tag.display_color,
                                    color: '#000000',
                                    boxShadow: `0 1px 2px ${tag.display_color}40`
                                }">
                                {{ tag.name }}
                            </span>
                        </div>
    
                        <div class="card__delivery-tag-status--mobile" :class="getRightTagColor()" v-if="props.rightTagText">
                            {{ props.rightTagText.toUpperCase() }}
                        </div>
                        
                    </div>
                    <div class="card__hover-content">
                        <div class="card__edit-button" v-if="showEdit" @click="handleEdit">
                            <i class="mdi mdi-pencil"></i>
                            Edit
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.card {
    @apply w-full bg-echoes-background-black rounded-2xl overflow-hidden transition-all duration-300 border border-echoes-border-gray;
   
    opacity: 0;
    will-change: transform, opacity;
}

.card:hover {
    @apply border-echoes-primary shadow-sm shadow-echoes-primary/50;
    transform: translateY(-6px);
  
}

.card:hover .card__title {
    @apply text-white;
}

.card--visible {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    transition-delay: calc(var(--card-index) * 100ms);
}

.card__content {
    @apply flex flex-row md:flex-col;
}

.card__image {
    @apply aspect-square bg-cover bg-center relative overflow-hidden w-1/3 md:w-full;
    background-color: #f3f4f6;
}

.card__image--placeholder {
    @apply flex items-center justify-center bg-echoes-background-black md:border-b md:border-echoes-border-gray;
}

.card__placeholder-icon {
    @apply text-echoes-background-gray;
}

.card__info {
    @apply px-2 py-1 md:p-4 flex flex-col justify-center;
}

.card__title {
    @apply text-white font-medium line-clamp-2 break-words overflow-hidden max-h-[48px] truncate max-w-full;
}

.card__price-row {
    @apply flex items-center justify-between mt-1;
}

.card__price {
    @apply text-xs md:text-sm text-echoes-primary font-medium;
}

.card__file-info {
    @apply mt-2 space-y-0.5;
}

.card__file-name {
    @apply text-[10px] md:text-sm text-white font-medium truncate;
}

.card__file-size {
    @apply text-[10px] md:text-xs text-echoes-primary font-medium;
}

.card__delivery-header {
    @apply absolute top-2 z-10 w-full px-1;
}

.card__delivery-tag {
    @apply text-xs font-semibold py-1 px-2 rounded-full text-white shadow-sm z-10;
    backdrop-filter: blur(4px);
}

.card__delivery-tag-status {
    @apply text-xs font-semibold py-1 px-2 rounded-full text-white shadow-sm z-10 hidden md:block;
    backdrop-filter: blur(4px);
}

.card__delivery-tag-status--mobile {
    @apply text-xs font-semibold py-1 px-2 rounded-full text-white shadow-sm z-10 block md:hidden absolute top-2 right-2;
    backdrop-filter: blur(4px);
}

.card__status {
    @apply text-xs font-medium py-1 px-2 rounded-full;
}

.card__status--active {
    @apply bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300;
}

.card__status--sold {
    @apply bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300;
}

.card__status--cancelled {
    @apply bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300;
}

.card--add {
    @apply bg-transparent flex items-center justify-center border-2 border-dashed border-echoes-primary/50  md:min-h-[360px];
    box-shadow: none;
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
        border-color 0.3s ease,
        background-color 0.3s ease;
}

.card--add:hover {
    @apply border-echoes-primary bg-echoes-background-black  text-white cursor-pointer;
    transform: translateY(-6px);
    
}

.card--add:hover .card__add-button {
    @apply bg-echoes-primary;
    transform: rotate(90deg);
}

.card--add:hover .card__add-text {
    @apply text-white;
}

.card__add-content {
    @apply flex flex-col items-center justify-center h-full gap-3;
    aspect-ratio: 1/1;
}

.card__add-button {
    @apply flex items-center justify-center w-12 h-12 rounded-full bg-echoes-primary/50  cursor-pointer;
    transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
        background-color 0.3s ease;
}

.card__add-icon {
    @apply text-2xl font-light text-white;
}

.card__add-text {
    @apply text-sm font-medium text-white text-center px-4;
}

.card__overlay {
    @apply absolute inset-0 bg-black/50 opacity-0 transition-opacity duration-300 z-[1];
    pointer-events: none;
}

.card:hover .card__overlay {
    @apply opacity-100;
    backdrop-filter: blur(4px);
}

.card__hover-content {
    @apply absolute inset-0 flex flex-col items-center justify-center opacity-0 pointer-events-none;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    z-index: 2;
}

.card:hover .card__hover-content {
    @apply opacity-100 pointer-events-auto;
}

.card__edit-button {
    @apply cursor-pointer px-4 py-1.5 bg-echoes-primary text-white rounded-full font-medium text-sm inline-flex items-center gap-2 absolute top-1/2 left-1/2;
    transform: translate(-50%, -50%) translateY(4px);
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.card__edit-button:hover {
    @apply bg-echoes-primary/50;
   
}

.card__edit-button:before {
    display: none;
}

.card__edit-button:hover:before {
    display: none;
}

.card__description-overlay {
    @apply absolute inset-0 bg-black/60 p-3 opacity-0 flex items-center justify-center;
    transition: opacity 0.3s;
    pointer-events: none;
    z-index: 1;
}

.card:hover .card__description-overlay {
    @apply opacity-0;
}

.card__description-text {
    @apply text-white text-sm font-medium text-center;
    display: -webkit-box;
    -webkit-line-clamp: 6;
    line-clamp: 6;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
}
</style>